import { BarChart3, Trophy } from 'lucide-react';
/**
 * Per-campaign analytics dashboard. Pulls from
 * GET /api/marketing/campaigns/:id/analytics and renders totals + per-step
 * A/B variant performance + attribution.
 */
import { useCallback, useEffect, useState } from 'react';

interface Variant {
  id: string;
  label: string;
  sent: number;
  opened: number;
  clicked: number;
  isWinner: boolean;
}

interface StepStat {
  stepId: string;
  stepOrder: number;
  channel: string;
  total: number;
  delivered: number;
  opens: number;
  clicks: number;
  variants: Variant[];
}

interface AnalyticsPayload {
  totals: { sends: number; delivered: number; opens: number; clicks: number };
  enrolments: { active: number; completed: number; aborted: number };
  attribution: { delivered: number; converted: number };
  steps: StepStat[];
}

function pct(n: number, d: number): string {
  if (d === 0) return '0%';
  return `${Math.round((n / d) * 100)}%`;
}

export function CampaignAnalytics({ campaignId }: { campaignId: string }) {
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/marketing/campaigns/${campaignId}/analytics`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setError(`Failed (${res.status})`);
        return;
      }
      const body = (await res.json()) as AnalyticsPayload;
      setData(body);
      setError(null);
    } catch {
      setError('Network error');
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <div className="text-sm text-red-700">{error}</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

  const { totals, enrolments, attribution, steps } = data;
  if (!totals || !enrolments || !attribution)
    return <div className="text-sm text-muted-foreground">Loading analytics…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <BarChart3 className="size-4" /> Analytics
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Sends" value={totals.sends} />
        <Stat
          label="Delivered"
          value={totals.delivered}
          sub={pct(totals.delivered, totals.sends)}
        />
        <Stat label="Opens" value={totals.opens} sub={pct(totals.opens, totals.delivered)} />
        <Stat label="Clicks" value={totals.clicks} sub={pct(totals.clicks, totals.delivered)} />
        <Stat label="Active in journey" value={enrolments.active} />
        <Stat label="Completed" value={enrolments.completed} />
        <Stat label="Aborted" value={enrolments.aborted} />
        <Stat
          label="Conversions (14d)"
          value={attribution.converted}
          sub={pct(attribution.converted, attribution.delivered)}
        />
      </div>

      {steps.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium uppercase text-muted-foreground">Per step</div>
          {steps.map((s) => (
            <div key={s.stepId} className="rounded border border-border p-3 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="num">
                  Step {s.stepOrder + 1} · {s.channel}
                </div>
                <div className="text-xs text-muted-foreground num">
                  {s.delivered}/{s.total} delivered · {pct(s.opens, s.delivered)} open ·{' '}
                  {pct(s.clicks, s.delivered)} click
                </div>
              </div>
              {s.variants.length > 0 && (
                <ul className="text-xs space-y-1">
                  {s.variants.map((v) => (
                    <li
                      key={v.id}
                      className={`flex items-center justify-between rounded border px-2 py-1 num ${
                        v.isWinner ? 'border-emerald-500 bg-emerald-50' : 'border-border'
                      }`}
                    >
                      <span>
                        Variant {v.label}
                        {v.isWinner && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-700">
                            <Trophy className="size-3" strokeWidth={1.75} />
                            winner
                          </span>
                        )}
                      </span>
                      <span>
                        {v.sent} sent · {pct(v.opened, v.sent)} open · {pct(v.clicked, v.sent)}{' '}
                        click
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: number | undefined; sub?: string }) {
  return (
    <div className="rounded border border-border bg-background p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold num">{(value ?? 0).toLocaleString()}</div>
      {sub && <div className="text-xs text-muted-foreground num">{sub}</div>}
    </div>
  );
}
